import { useState, useEffect, useRef } from 'react';
import Header from '../authentication/Header';
import Footer from '../authentication/Footer';
import browserHistory from '../../history';
import './Home.css';

const GENRES = ['Action', 'Animation', 'Comedy', 'Documentary', 'Drama', 'Fantasy', 'Horror', 'Mystery', 'Reality Show', 'Sports-Fitness'];

const HEIRLOOM_CAROUSELS = [
  { title: 'Thanksgiving',  carousel: 'thanksgiving' },
  { title: 'Birthdays',     carousel: 'birthdays' },
  { title: 'Halloween',     carousel: 'halloween' },
  { title: 'Xmas',          carousel: 'christmas day' },
  { title: 'Xmas Eve',      carousel: 'christmas eve' },
  { title: 'Easter',        carousel: 'easter' },
  { title: 'Mothers Day',   carousel: 'mothers day' },
];

const getBase = () => (window.site && window.site.screeningApiBaseUrl) || 'https://content-screening-api.dev2.envoi.cloud';
const getApp = () => (window.app && window.app.appName) || 'screenixx';
const getUrlApp = () => (window.app && (window.app.urlAppName || window.app.appName)) || 'screenixx';
const getEnv = () => (window.app && window.app.appEnvName) || 'dev';

async function fetchAssets(extra = {}) {
  const q = new URLSearchParams({ env: getEnv(), appname: getApp(), userAssetinfo: 'false', assetcount: '48', pageNumber: '1', ...extra });
  const res = await fetch(`${getBase()}/assets?${q}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const data = await res.json();
  console.log('Fetched assets with', extra, data);
  return data?.result?.Items || [];
}

function getPortraitImage(asset) {
  const config = (window.site && window.site.config) || {};
  const cdn = config.sourceCloudFront || '';
  const suffix = '?auto=compress,format&width=300';
  const portraitPath = asset?.destinations?.uploads?.envoi?.envoi_portrait?.filePath;
  if (cdn && portraitPath) return cdn + '/' + portraitPath.replace(/ /g, '%20') + suffix;
  const fallback = asset?.thumbnail || asset?.portrait_logo_image || config.defaultLandscape || '';
  return fallback.replace(/ /g, '%20') + suffix;
  
}

function getLandscapeImage(asset) {
  const config = (window.site && window.site.config) || {};
  const cdn = config.sourceCloudFront || '';
  const suffix = '?auto=compress,format&width=1280';
  const landscapePath = asset?.destinations?.uploads?.envoi?.envoi_landscape?.filePath;
  if (cdn && landscapePath) return cdn + '/' + landscapePath.replace(/ /g, '%20') + suffix;
  const fallback = asset?.thumbnail || asset?.landscape_logo_image || asset?.portrait_logo_image || config.defaultLandscape || '';
  return fallback.replace(/ /g, '%20') + suffix;
}

function getTitle(asset) {
  return asset?.assetname || asset?.title || asset?.name || '';
}

function buildCrewList(crew) {
  if (!crew || !crew.length) return {};
  return crew.reduce((acc, m) => {
    const role = m.crewrole || m.role || 'Crew';
    if (!acc[role]) acc[role] = [];
    acc[role].push(m.crew || m.name || '');
    return acc;
  }, {});
}

// ── Featured Banner ────────────────────────────────────────────────────────
function FeaturedBanner({ assets, loading }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!assets.length) return;
    const t = setInterval(() => setIdx(i => (i + 1) % assets.length), 6000);
    return () => clearInterval(t);
  }, [assets.length]);

  if (loading) return <div className="featured-skeleton" />;
  if (!assets.length) return null;

  const item = assets[idx];
  const prev = () => setIdx(i => (i - 1 + assets.length) % assets.length);
  const next = () => setIdx(i => (i + 1) % assets.length);

  const goToAsset = () => browserHistory.push({
    pathname: '/asset',
    search: `?assetid=${item.assetid}&appname=${getUrlApp()}`,
    state: { assetType: item.assetType },
  });

  // badges: rating, genre, year, resolution, duration
  const badges = [
    item?.rating && item.rating !== 'none' ? item.rating : null,
    item?.genre,
    item?.releaseDate ? new Date(item.releaseDate).getFullYear() : (item?.year || null),
    item?.resolution || item?.videoResolution || item?.quality || null,
    item?.duration || null,
  ].filter(Boolean);

  // cast — first 3 names
  const castNames = (item?.cast || [])
    .slice(0, 3)
    .map(c => c.cast || c.castrole || '')
    .filter(Boolean)
    .join(', ');

  // crew — first 3 roles
  const crewList = buildCrewList(item?.crew);
  const crewRoles = Object.keys(crewList).slice(0, 3);

  const fullAssetname = item?.assetname || getTitle(item);
  const assetname = fullAssetname.length > 45 ? fullAssetname.substring(0, 45) + '...' : fullAssetname;

  // trailer path — same logic as AssetInfoPage
  const t1 = item?.trailers?.trailer1;
  const trailerPath = t1 && (t1.m3u8_url || t1.vdms_url || t1.iconik_proxy_url || t1.eluvio_url || t1.Mp4_proxy_URL || t1.safestreamUrl);

  // play button condition — same logic as AssetInfoPage
  const avail = item?.availability;
  const showPlay = item?.assetType !== 'Episodic' && (
    !avail ||
    (avail?.buyplans && Object.keys(avail.buyplans).length > 0 && (item?.buy === true || item?.gift === true || item?.rent === true)) ||
    item?.preview?.enable ||
    avail?.free ||
    avail?.publicViewing === 'true' || avail?.publicViewing === true
  );

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
  const bannerImage = isMobile ? getPortraitImage(item) : getLandscapeImage(item);

  return (
    <div className="featured-banner" style={{ backgroundImage: `url(${bannerImage})` }}>
      <div className="featured-grey-left" />
      <div className="featured-overlay">
        <div className="featured-content">
          {item?.logo_image
            ? <img src={item.logo_image} alt={fullAssetname} className="featured-logo" />
            : <h1 className="featured-title">{item?.artistname}<span title={fullAssetname}>{assetname}</span></h1>
          }

          {badges.length > 0 && (
            <div className="featured-badges">
              {badges.map((b, i) => <span key={i} className="featured-badge">{b}</span>)}
            </div>
          )}

          {item?.description && (
            <p className="featured-desc">{item.description}</p>
          )}

          {castNames && (
            <p className="featured-crew-line">
              <span className="featured-crew-label">Starring: </span>
              <span className="featured-crew-value">{castNames}</span>
            </p>
          )}

          {crewRoles.map(role => (
            <p key={role} className="featured-crew-line">
              <span className="featured-crew-label">{role}: </span>
              <span className="featured-crew-value">{crewList[role].join(', ')}</span>
            </p>
          ))}

          
            <div className="featured-actions">
             
                <button className="featured-btn featured-btn--play" onClick={goToAsset}>
                  <i className="material-icons mr-2">arrow_right_alt</i> Details
                </button>
            
             
            </div>
         
        </div>

        <div className="featured-bottom">
          <div className="featured-dots">
            {assets.map((_, i) => (
              <button
                key={i}
                className={`featured-dot${i === idx ? ' active' : ''}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
          <div className="featured-arrows">
            <button className="featured-arrow" onClick={prev}>&#8249;</button>
            <button className="featured-arrow" onClick={next}>&#8250;</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Content Row ────────────────────────────────────────────────────────────
function ContentRow({ title, extra, fallbackExtra, limit, landscape }) {
  const trackRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const extraRef = useRef(extra);
  const fallbackExtraRef = useRef(fallbackExtra);
  const pendingScrollRef = useRef(false);

  useEffect(() => {
    const count = limit || 12;
    fetchAssets({ ...extraRef.current, assetcount: String(count), pageNumber: '1' })
      .then(result => {
        if (result.length === 0 && fallbackExtraRef.current) {
          extraRef.current = fallbackExtraRef.current;
          return fetchAssets({ ...extraRef.current, assetcount: String(count), pageNumber: '1' });
        }
        return result;
      })
      .then(result => {
        setAssets(limit ? result.slice(0, limit) : result);
        if (limit || result.length < count) hasMoreRef.current = false;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Re-attach scroll listener after initial load completes
  useEffect(() => {
    if (loading) return;
    const el = trackRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(hasMoreRef.current || el.scrollLeft + el.offsetWidth < el.scrollWidth - 10);
    };
    el.addEventListener('scroll', check, { passive: true });
    check();
    return () => el.removeEventListener('scroll', check);
  }, [loading]); // eslint-disable-line

  const loadMore = () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    fetchAssets({ ...extraRef.current, assetcount: '12', pageNumber: String(nextPage) })
      .then(more => {
        if (more.length > 0) {
          setAssets(prev => [...prev, ...more]);
          pageRef.current = nextPage;
          if (more.length < 12) hasMoreRef.current = false;
        } else {
          hasMoreRef.current = false;
        }
      })
      .catch(() => { hasMoreRef.current = false; })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        // Wait for React to paint new cards before re-checking scrollWidth
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const el = trackRef.current;
          if (!el) return;
          setCanScrollRight(hasMoreRef.current || el.scrollLeft + el.offsetWidth < el.scrollWidth - 10);
          // Execute the deferred scroll that triggered this load
          if (pendingScrollRef.current) {
            pendingScrollRef.current = false;
            el.scrollBy({ left: el.offsetWidth, behavior: 'smooth' });
          }
        }));
      });
  };

  const scroll = (dir) => {
    // Ignore clicks during initial load to avoid racing with page-1 fetch
    if (!trackRef.current || loading) return;
    const { offsetWidth, scrollLeft, scrollWidth } = trackRef.current;
    // At the end of loaded assets — fetch next page, defer the scroll until loaded
    if (dir > 0 && scrollLeft + offsetWidth >= scrollWidth - 10) {
      pendingScrollRef.current = true;
      loadMore();
      return;
    }
    trackRef.current.scrollBy({ left: dir * offsetWidth, behavior: 'smooth' });
  };

  const skeletons = Array.from({ length: 6 });

  if (!loading && !assets.length) return null;

  return (
    <div className="home-row">
      <h2 className="row-title">{title}</h2>
      <div className="row-wrapper">
        <button
          className="row-arrow row-arrow--left"
          onClick={() => scroll(-1)}
          disabled={!canScrollLeft}
        >
          <i className="material-icons">chevron_left</i>
        </button>
        <div className="row-track" ref={trackRef}>
          {loading
            ? skeletons.map((_, i) => (
                <div key={i} className="row-card row-card--skeleton">
                  <div className="row-card-img-wrap" />
                </div>
              ))
            : assets.map((asset, i) => (
                <div
                  className="row-card"
                  key={asset._id || i}
                  onClick={() => browserHistory.push({
                    pathname: '/asset',
                    search: `?assetid=${asset.assetid}&appname=${getUrlApp()}`,
                    state: { assetType: asset.assetType },
                  })}
                >
                  <div className="row-card-img-wrap">
                    <img
                      src={landscape ? getLandscapeImage(asset) : getPortraitImage(asset)}
                      alt={getTitle(asset)}
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {asset?.isMembersOnly && <span className="row-card-badge">Members Only</span>}
                  </div>
                  <p className="row-card-title" title={getTitle(asset)}>
                    {getTitle(asset)}
                  </p>
                </div>
              ))
          }
          {loadingMore && skeletons.map((_, i) => (
            <div key={`more-${i}`} className="row-card row-card--skeleton">
              <div className="row-card-img-wrap" />
            </div>
          ))}
        </div>
        <button
          className="row-arrow row-arrow--right"
          onClick={() => scroll(1)}
          disabled={!canScrollRight}
        >
          <i className="material-icons">chevron_right</i>
        </button>
      </div>
    </div>
  );
}

// ── Home Page ──────────────────────────────────────────────────────────────
export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  useEffect(() => {
    const isHeirloom = window.app && window.app.urlAppName === 'heirloom';
    fetchAssets({ carousel: 'Films', isFeatured: 'true', assetcount: isHeirloom ? '12' : '48' })
      .then(feat => setFeatured(feat))
      .catch(() => {})
      .finally(() => setLoadingFeatured(false));
  }, []);

  return (
    <div className="home-page">
      <Header />
      <main className="home-main">
        <FeaturedBanner assets={featured} loading={loadingFeatured} />
        <div className="home-rows">

           {(window.app && window.app.urlAppName) != 'heirloom' && (<>
          <ContentRow title="Trailers" extra={{ carousel: 'Commercials' }} />
          <ContentRow title="Top 10 TV Shows" extra={{ carousel: 'Show' }} fallbackExtra={{ carousel: 'TV Shows' }} limit={10} />


          <ContentRow title="Top 10 Movies" extra={{ carousel: 'Films' }} fallbackExtra={{ carousel: 'Movies' }} limit={10} />
          <ContentRow title="Interviews" extra={{ carousel: 'Interviews' }} />
          </>)}
          
          {(window.app && window.app.urlAppName) === 'heirloom'
            ? HEIRLOOM_CAROUSELS.map(({ title, carousel }) => (
                <ContentRow key={carousel} title={title} extra={{ carousel }} landscape />
              ))
            : GENRES.map(genre => (
                <ContentRow
                  key={genre}
                  title={genre}
                  extra={{ carousel: 'Films', genreValue: 'true', genre }}
                />
              ))
          }
        </div>
      </main>
      <Footer />
    </div>
  );
}
